import { useEffect, useState, type ReactNode } from 'react';
import { getPhotoUrl } from '../api/attendance';

/**
 * El panel que se abre debajo de una fila para mostrar su detalle.
 *
 * Existe porque el usuario no distinguía que se hubiera abierto nada: *«siento que sigue siendo la
 * misma pantalla, como que no fuera otra cosa… tiene que estar claro de que no forma parte de la
 * pantalla que estabas viendo»*. Y no era una impresión suya: **la fila desplegada era `bg-gray-50`
 * sobre un shell de panel que también es `bg-gray-50`**. El único resaltado que existía tenía
 * contraste cero contra el fondo de la página. Sumado a eso, el borde del panel era el mismo
 * `border-gray-100` que separa dos filas cualesquiera, no había sangría, y el único indicador
 * fiable era un «ver todo» de 12 px en el extremo derecho.
 *
 * Las cuatro señales que ahora dicen «esto es otra cosa», y por qué hacen falta las cuatro:
 *
 *  1. **Un canal más oscuro** (`bg-gray-200/70`) detrás del panel. Es lo que rompe el empate con el
 *     fondo de la página; sin esto, todo lo demás flota sobre nada.
 *  2. **Una tarjeta blanca con borde propio**, más marcado que el separador de filas. La tabla
 *     ahora es blanca, así que el panel se distingue por estar *contenido*, no por ser más gris.
 *  3. **Sangría**. El panel no llega a los bordes de la tabla: se ve colgando de su fila.
 *  4. **Encabezado con nombre y sujeto** («Detalle del cierre · Makro Surco · 05/09»), más el botón
 *     de cerrar. Es lo que hace que alguien que entra por primera vez sepa qué está mirando.
 *
 * ⚠️ Deliberadamente NO lleva una barra de acento vertical a la izquierda. Es el recurso obvio para
 * «esto cuelga de aquello» y es exactamente por eso que se lee como plantilla; la jerarquía la dan
 * la superficie, el encierro y el aire.
 */
export function PanelDeDetalle({ titulo, sujeto, onCerrar, children }: {
  /** Qué es esto: «Detalle del cierre», «Detalle de la visita». */
  titulo: string;
  /** De quién o de qué: la tienda y la fecha, el trabajador. Lo que ancla el panel a su fila. */
  sujeto: string;
  onCerrar: () => void;
  children: ReactNode;
}) {
  return (
    <div className="bg-gray-200/70 px-3 py-3 sm:px-6 sm:py-4">
      <section
        aria-label={`${titulo}: ${sujeto}`}
        className="rounded-lg border border-gray-300 bg-white shadow-sm"
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-gray-900">{titulo}</h3>
            <p className="truncate text-xs text-gray-600">{sujeto}</p>
          </div>
          {/* Segundo camino para cerrar, además del enlace de la fila. Quien bajó leyendo un panel
              largo no tiene por qué volver a subir para encontrar el botón que lo abrió. */}
          <button
            type="button"
            onClick={onCerrar}
            className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            Cerrar
          </button>
        </header>
        {/* `space-y-5` entre secciones contra el `space-y-1` de adentro: el ritmo —apretado dentro
            de un grupo, suelto entre grupos— es lo que deja ver la estructura al entrecerrar. */}
        <div className="space-y-5 px-4 py-4">{children}</div>
      </section>
    </div>
  );
}

/**
 * Un grupo con nombre dentro del panel.
 *
 * El encabezado usa un estilo distinto del de las etiquetas de campo a propósito: antes los dos
 * eran `text-xs text-gray-500` y por eso el dato principal del formulario pesaba lo mismo que un
 * campo secundario. `ancho` lo saca de las dos columnas cuando el contenido es texto largo.
 */
export function Seccion({ titulo, extra, ancho, children }: {
  titulo: string;
  /** Alineado a la derecha del encabezado: el total, un contador. El ancla al entrecerrar. */
  extra?: ReactNode;
  ancho?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={ancho === true ? 'md:col-span-2' : undefined}>
      <div className="mb-2 flex items-baseline justify-between gap-3 border-b border-gray-100 pb-1">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{titulo}</h4>
        {extra}
      </div>
      {children}
    </section>
  );
}

/** Un dato con su rótulo. El rótulo se lee como rótulo y el valor como valor: no son lo mismo. */
export function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{etiqueta}</dt>
      <dd className="text-sm text-gray-900">{children}</dd>
    </div>
  );
}

/**
 * Una señal del registro: «tardío», «sin GPS», «quiebre».
 *
 * Es una pastilla y no texto coloreado suelto porque seis señales seguidas a 12 px, separadas solo
 * por un margen, se leen como una oración rota — y cuando la fila no se puede leer de un vistazo,
 * todo el mundo termina abriendo el panel. Las bandejas de canjes y el tablero de Hoy ya usaban
 * exactamente esta pastilla; esto la vuelve una sola.
 */
export function Senal({ tono = 'aviso', children }: {
  tono?: 'aviso' | 'grave' | 'neutro';
  children: ReactNode;
}) {
  const cls = {
    aviso: 'bg-amber-100 text-amber-800',
    grave: 'bg-red-100 text-red-800',
    neutro: 'bg-gray-100 text-gray-700',
  }[tono];
  return <span className={`rounded px-1.5 py-0.5 text-xs ${cls}`}>{children}</span>;
}

/**
 * El botón que abre y cierra el detalle de una fila.
 *
 * Lleva `aria-expanded` —que faltaba en dos de las tres bandejas— y un chevron que rota: el estado
 * abierto/cerrado tiene que verse sin leer la palabra, sobre todo cuando el panel es alto y el
 * botón queda lejos de lo que abrió.
 */
export function BotonDetalle({ abierto, onClick }: { abierto: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={abierto}
      className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-blue-700 hover:bg-blue-50"
    >
      {abierto ? 'Ocultar' : 'Ver detalle'}
      <svg
        viewBox="0 0 20 20" aria-hidden="true" fill="currentColor"
        className={`h-3.5 w-3.5 transition-transform duration-150 ${abierto ? 'rotate-180' : ''}`}
      >
        <path d="M5.5 7.5 10 12l4.5-4.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/** La clase de la fila que está abierta. Sin esto, con un panel alto se pierde cuál abriste. */
export const FILA_ABIERTA = 'bg-blue-50';

/**
 * Una sección que nace PLEGADA, con su conteo a la vista.
 *
 * Un puesto de mercado puede traer cuarenta precios y cuarenta y cuatro marcas. Dibujados todos
 * abiertos, el panel de detalle deja de ser un detalle y pasa a ser un volcado: nadie lo lee, y lo
 * que se busca queda a seis pantallazos de distancia. Plegado con el conteo al lado —«Precios ·
 * 23»— la misma información se puede recorrer con la vista y abrir solo lo que hace falta.
 *
 * Vive acá y no adentro de cada pantalla por lo mismo que dice el encabezado del archivo: dos
 * acordeones escritos por separado se ven distinto en tres meses. Este proyecto ya arrastró tres
 * copias de las tarjetas de indicador con dos tipografías distintas.
 */
export function SeccionPlegable({ titulo, extra, abiertaPorDefecto = false, ancho, children }: {
  titulo: string;
  /** El conteo. Es lo que hace útil una sección cerrada: sin él, plegar esconde. */
  extra?: ReactNode;
  abiertaPorDefecto?: boolean;
  ancho?: boolean;
  children: ReactNode;
}) {
  const [abierta, setAbierta] = useState(abiertaPorDefecto);
  return (
    <section className={ancho === true ? 'md:col-span-2' : undefined}>
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        aria-expanded={abierta}
        className="mb-2 flex w-full items-baseline justify-between gap-3 border-b border-gray-100 pb-1 text-left"
      >
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {titulo}
          <span aria-hidden="true" className="ml-1 text-gray-400">{abierta ? '▾' : '▸'}</span>
        </h4>
        {extra}
      </button>
      {abierta && children}
    </section>
  );
}

/**
 * Una foto del bucket privado, firmada al montar.
 *
 * La firma dura 60 s, así que pedirlas todas al cargar la tabla dejaría vencidas las de abajo antes
 * de que alguien baje a mirarlas, y además firmaría decenas de rutas que nadie va a abrir. Este
 * componente se monta recién cuando la fila se expande.
 *
 * ⚠️ **«Sin foto» y «no se pudo cargar» se distinguen a propósito.** La foto casi nunca es
 * obligatoria —hay tiendas que no dejan sacar el celular, y en canal moderno «Me pidieron no
 * fotografiar» es una respuesta del formulario— así que a quien revisa le sirve saber si está ante
 * un registro sin evidencia o ante una evidencia que existe y no se ve. Son dos conversaciones
 * distintas: una con la cadena, la otra con sistemas.
 *
 * Se muestra GRANDE y no como miniatura: es el contenido más valioso del panel, y a 128 px no se
 * distingue una góndola de otra, que es lo único que hay que mirar.
 */
export function FotoPrivada({ path, alt }: { path: string; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [falla, setFalla] = useState(false);

  useEffect(() => {
    let vivo = true;
    void getPhotoUrl(path)
      .then((u) => { if (vivo) setUrl(u); })
      .catch(() => { if (vivo) setFalla(true); });
    return () => { vivo = false; };
  }, [path]);

  if (falla) {
    return (
      <p className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
        La foto existe pero no se pudo cargar.
      </p>
    );
  }
  if (url === null) {
    return <div className="h-72 w-56 animate-pulse rounded border border-gray-200 bg-gray-100" />;
  }
  return (
    <img
      src={url} alt={alt} onError={() => setFalla(true)}
      className="max-h-96 w-auto max-w-full rounded border border-gray-200 bg-gray-50 object-contain"
    />
  );
}
